// TEST-EXEMPT: Visual/animation-only component with no testable logic
import { Suspense, useRef, useEffect, useMemo, Component, type ReactNode } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, Float } from '@react-three/drei'

import * as THREE from 'three'

/* ── Silent error boundary for 3D model loading ─────── */
// Catches GLB load failures (404s, context loss, parse errors) and renders
// nothing instead of propagating to the global ErrorBoundary, which would
// show a full-screen error and retry in a loop, leaking WebGL contexts.
class ModelErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(err: Error) {
    // Log quietly — don't re-throw
    console.warn('[PhyreModel3D] 3D load error (handled):', err.message)
  }
  render() {
    if (this.state.hasError) return this.props.fallback ?? null
    return this.props.children
  }
}

function useIcyGradientMaterial(colorA: string, colorB: string, colorC: string) {
  return useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColorA: { value: new THREE.Color(colorA) },
        uColorB: { value: new THREE.Color(colorB) },
        uColorC: { value: new THREE.Color(colorC) },
      },
      vertexShader: `
        varying vec3 vPosition;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;

        void main() {
          vPosition = position;
          vNormal = normalize(normalMatrix * normal);
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPos.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;

        varying vec3 vPosition;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;

        void main() {
          // Gradient maps exactly across model X axis
          // Vertical gradient — top (positive Y) = colorA (pink wings), bottom (negative Y) = colorC (ice flames)
          float gradientPos = clamp(-vWorldPosition.y * 0.4 + 0.5, 0.0, 1.0);

          // Three-stop gradient — diamond tint color
          vec3 baseColor;
          if (gradientPos < 0.5) {
            baseColor = mix(uColorA, uColorB, gradientPos * 2.0);
          } else {
            baseColor = mix(uColorB, uColorC, (gradientPos - 0.5) * 2.0);
          }

          // View direction for fresnel & specular
          vec3 viewDir = normalize(cameraPosition - vWorldPosition);
          float NdotV = max(dot(vNormal, viewDir), 0.0);

          // Diamond base — deep tinted body with high clarity
          vec3 color = baseColor * 0.6;

          // Strong fresnel — diamonds reflect more at edges (high IOR ~2.42)
          float fresnel = pow(1.0 - NdotV, 4.0);
          color += mix(baseColor * 1.5, vec3(1.0), 0.6) * fresnel * 0.8;

          // Multiple light sources for faceted look
          vec3 lights[3];
          lights[0] = normalize(vec3(1.0, 1.0, 0.5));
          lights[1] = normalize(vec3(-0.5, 0.8, 1.0));
          lights[2] = normalize(vec3(0.0, -0.5, 1.0));

          for (int i = 0; i < 3; i++) {
            float NdotL = max(dot(vNormal, lights[i]), 0.0);
            color += baseColor * NdotL * 0.15;

            vec3 halfDir = normalize(lights[i] + viewDir);
            float spec = pow(max(dot(vNormal, halfDir), 0.0), 128.0);
            color += vec3(1.0) * spec * 0.9;

            float spec2 = pow(max(dot(vNormal, halfDir), 0.0), 32.0);
            color += baseColor * spec2 * 0.3;
          }

          // Rainbow dispersion at glancing angles
          float dispersion = pow(1.0 - NdotV, 3.0);
          vec3 rainbow = vec3(
            sin(vWorldPosition.x * 8.0 + uTime * 0.5) * 0.5 + 0.5,
            sin(vWorldPosition.x * 8.0 + uTime * 0.5 + 2.094) * 0.5 + 0.5,
            sin(vWorldPosition.x * 8.0 + uTime * 0.5 + 4.189) * 0.5 + 0.5
          );
          color += rainbow * dispersion * 0.15;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    })
  }, [colorA, colorB, colorC])
}

/* ── Inner scene: loads + auto-fits the GLB ──────────── */
function Model({
  url,
  autoRotate,
  colorA,
  colorB,
  colorC,
}: {
  url: string
  autoRotate: boolean
  colorA: string
  colorB: string
  colorC: string
}) {
  const { scene: rawScene } = useGLTF(url)
  const groupRef = useRef<THREE.Group>(null)
  const materialRef = useRef<THREE.ShaderMaterial | null>(null)
  const gradientMat = useIcyGradientMaterial(colorA, colorB, colorC)

  // Clone the scene so each PhyreModel3D instance owns its own Three.js object
  // graph. Without cloning, useGLTF returns the same cached scene for every
  // instance sharing the same URL. Three.js objects can only belong to one
  // parent at a time, so two <primitive object={scene} /> calls fight over the
  // same object — whichever canvas gets it last "wins" and the other goes blank.
  // Cloning also prevents the double-scaling bug: the second instance's
  // useEffect would otherwise compute scaleFactor=1 on an already-scaled scene.
  const scene = useMemo(() => rawScene.clone(true), [rawScene])

  // Auto-fit: measure bounding box, center + scale to fill view
  useEffect(() => {
    const box = new THREE.Box3().setFromObject(scene)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())

    // Center the model at origin
    scene.position.sub(center)

    // Scale to fit within a ~3 unit sphere
    const maxDim = Math.max(size.x, size.y, size.z)
    const targetSize = 3
    const scaleFactor = maxDim > 0 ? targetSize / maxDim : 1
    scene.scale.setScalar(scaleFactor)

    // Apply gradient material to all meshes
    materialRef.current = gradientMat
    scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh
        mesh.material = gradientMat
      }
    })
  }, [scene, gradientMat])

  // Animate: rotate + update shader time
  useFrame((state, delta) => {
    if (groupRef.current && autoRotate) {
      groupRef.current.rotation.y += delta * 0.3
    }
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime
    }
  })

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.5}>
      <group ref={groupRef}>
        <primitive object={scene} />
      </group>
    </Float>
  )
}

/* ── Props ────────────────────────────────────────────── */
interface PhyreModel3DProps {
  className?: string
  autoRotate?: boolean
  url?: string
  /** Three-stop gradient colors: primary → mid → accent */
  colorA?: string
  colorB?: string
  colorC?: string
  /** Primary light color */
  lightColor?: string
  /** Accent light color */
  accentColor?: string
}

/* ── Public component ────────────────────────────────── */
export default function PhyreModel3D({
  className = '',
  autoRotate = false,
  url = '/phyre-3d.glb',
  colorA = '#F01265',
  colorB = '#6D28D9',
  colorC = '#A8F0FF',
  lightColor = '#F01265',
  accentColor = '#7DD3FC',
}: PhyreModel3DProps) {
  return (
    <ModelErrorBoundary>
      <div className={`w-full h-full ${className}`}>
        <Canvas
          camera={{ position: [0, 0, 6], fov: 50 }}
          frameloop="demand"
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
            premultipliedAlpha: false,
          }}
          style={{ background: 'transparent' }}
          dpr={[1, 1.5]}
          onCreated={({ gl, scene }) => {
            gl.setClearColor(0x000000, 0)
            scene.background = null
          }}
        >
          <Suspense fallback={null}>
            {/* Lighting — themed to model colors */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={1.2} color={lightColor} />
            <directionalLight position={[-5, 3, -5]} intensity={0.8} color={accentColor} />
            <pointLight position={[0, -3, 2]} intensity={0.6} color={colorB} />
            <pointLight position={[0, 4, -2]} intensity={0.4} color={lightColor} />

            {/* The model — auto-fitted */}
            <Model
              url={url}
              autoRotate={autoRotate}
              colorA={colorA}
              colorB={colorB}
              colorC={colorC}
            />
          </Suspense>
        </Canvas>
      </div>
    </ModelErrorBoundary>
  )
}

// Call preloadModels() from specific pages that actually need the models
// eslint-disable-next-line react-refresh/only-export-components
export function preloadModels(paths: string[]) {
  paths.forEach((p) => useGLTF.preload(p))
}
